import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { loadSavedForm, saveForm } from './formUtils';

const DEFAULTS = {
  companyName: '',
  registrationNumber: '',
  annualTurnover: '',
  isEme: 'yes',
  blackOwnershipPercent: '',
  blackWomenOwnershipPercent: '',
  authorisedSignatoryName: '',
  authorisedSignatoryId: '',
  contactEmail: '',
  contactPhone: '',
};

const steps = ['Company', 'Ownership Declaration', 'Review'];

const BeeAffidavitForm = ({ serviceId, onComplete }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => loadSavedForm(serviceId) || DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveForm(serviceId, form);
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (step === 1 && (!form.companyName || !form.registrationNumber || !form.annualTurnover)) {
      toast({
        title: 'Required Fields Missing',
        description: 'Company name, registration number and annual turnover are required.',
        variant: 'destructive',
      });
      return false;
    }
    if (step === 2) {
      if (!form.blackOwnershipPercent || !form.authorisedSignatoryName || !form.contactEmail) {
        toast({
          title: 'Required Fields Missing',
          description: 'Black ownership %, authorised signatory and contact email are required.',
          variant: 'destructive',
        });
        return false;
      }
      const turnover = parseFloat(form.annualTurnover) || 0;
      if (form.isEme === 'yes' && turnover > 10_000_000) {
        toast({
          title: 'Not eligible as EME',
          description:
            'EME affidavit is only available for businesses with turnover ≤ R10 million.',
          variant: 'destructive',
        });
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validate()) return;
    onComplete(form);
  };

  return (
    <FormShell
      title="BEE Certificate (EME Affidavit)"
      stepLabels={steps}
      currentStep={step}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={saved}
    >
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Company Information</h3>
          <div>
            <Label>Registered Company Name *</Label>
            <Input value={form.companyName} onChange={(e) => set('companyName', e.target.value)} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>CIPC Registration Number *</Label>
              <Input
                value={form.registrationNumber}
                onChange={(e) => set('registrationNumber', e.target.value)}
              />
            </div>
            <div>
              <Label>Annual Turnover (R) *</Label>
              <Input
                type="number"
                value={form.annualTurnover}
                onChange={(e) => set('annualTurnover', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Are you applying as an Exempt Micro Enterprise (EME)?</Label>
            <select
              value={form.isEme}
              onChange={(e) => set('isEme', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="yes">Yes — turnover ≤ R10m</option>
              <option value="no">No</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              EME affidavits are only valid for businesses with annual turnover up to R10 million.
            </p>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Ownership Declaration</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Black Ownership % *</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={form.blackOwnershipPercent}
                onChange={(e) => set('blackOwnershipPercent', e.target.value)}
              />
            </div>
            <div>
              <Label>Black Women Ownership %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={form.blackWomenOwnershipPercent}
                onChange={(e) => set('blackWomenOwnershipPercent', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Authorised Signatory Name *</Label>
            <Input
              value={form.authorisedSignatoryName}
              onChange={(e) => set('authorisedSignatoryName', e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Signatory ID Number</Label>
              <Input
                value={form.authorisedSignatoryId}
                onChange={(e) => set('authorisedSignatoryId', e.target.value)}
              />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Contact Phone</Label>
            <Input
              value={form.contactPhone}
              onChange={(e) => set('contactPhone', e.target.value)}
            />
          </div>
          <p className="text-xs text-gray-500">
            By submitting, you confirm the information is true and that the affidavit will be
            sworn before a Commissioner of Oaths.
          </p>
        </div>
      )}

      {step === 3 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-800">Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Company:</strong> {form.companyName}</p>
            <p><strong>Reg No:</strong> {form.registrationNumber}</p>
            <p><strong>Turnover:</strong> R{form.annualTurnover}</p>
            <p><strong>EME:</strong> {form.isEme}</p>
            <p><strong>Black Ownership:</strong> {form.blackOwnershipPercent}%</p>
            <p><strong>Signatory:</strong> {form.authorisedSignatoryName}</p>
          </CardContent>
        </Card>
      )}
    </FormShell>
  );
};

export default BeeAffidavitForm;
